// overview.ts — pure sparkline/trend math behind the Overview screen's "Messages · last 14
// days" stat card (Task 8, handoff README §2). Extracted out of `BotOverview.tsx` so the shapes
// that decide what gets drawn have real tests instead of only ever being eyeballed in the
// browser — house pattern (`lib/usage.ts`, `lib/landing.ts`). `width`/`height` are parameters
// rather than module constants so the same math is trivially testable against small fixtures.
import type { BotOverviewDto, SourceDto } from '@repo/shared-types';
import type { StatusChipState } from '@repo/ui/components/status-chip';
import type { Catalog } from '../locales/en';
import { effectiveWorstStatus } from './bots-list.ts';
import { formatShortDate } from './usage.ts';

export interface DailyCount {
  date: string;
  count: number;
}

/** Which of three states `FirstRun.tsx`'s hero paragraph and Knowledge card should render
 * (Task 9 fix round 1). Before this, the hero's "It read N pages… and knows them well enough to
 * answer" sentence was driven only by `pageCount`, with no gate on `allReady` — the most common
 * moment this screen renders is right after onboarding, while a source can still be
 * `crawling`/`indexing` with `pageCount === 0`, so the sentence would overclaim right beside a
 * Knowledge card saying "Sources syncing". Driven only by fields already on
 * `BotOverviewDto['knowledge']` — no new signal.
 *
 * `'empty'` (`sourceCount === 0`) and `'indexing'` (`sourceCount > 0 && !allReady`) are distinct
 * because they're different facts: nothing was ever added, vs. something was added and isn't
 * done yet. `allReady` alone can't tell them apart — the repo layer (`apps/api/src/routes/
 * bots.ts`) defines `allReady = sourceCount > 0 && every source ready`, so it's `false` for both
 * a zero-source bot and a still-indexing one. */
export function firstRunKnowledgeState(
  knowledge: Pick<BotOverviewDto['knowledge'], 'sourceCount' | 'allReady'>,
): 'empty' | 'indexing' | 'ready' {
  if (knowledge.sourceCount === 0) return 'empty';
  if (!knowledge.allReady) return 'indexing';
  return 'ready';
}

/** `useBotOverview`'s poll-while-syncing stop condition (dashboard-shell-feedback fix-wave
 * item 1) — extracted so it has a real test instead of only ever being eyeballed in the
 * network panel. Poll while any source is still `pending`/`crawling`/`indexing`/`deleting`
 * (source-detail-diagnostics Task 4 added the last one — a teardown job is real work too, and
 * without it a delete stopped this poll before the job it has no other way of observing
 * finished); stop once nothing is in flight. That is also the correct answer for a zero-source
 * bot (nothing has ever started) and deliberately NOT keyed off `worstStatus === 'error'`:
 * severity ranks `error` above `crawling`, so a bot with one failed source and one still-crawling
 * source would stop the poll on the exact crawl it exists to watch. See
 * `BotOverviewDto['knowledge'].anyInFlight`'s doc comment for the full reasoning. */
export function shouldPollOverview(
  knowledge: Pick<BotOverviewDto['knowledge'], 'anyInFlight'> | undefined,
): boolean {
  return knowledge?.anyInFlight ?? false;
}

/** The Knowledge card's `StatusChip` state + label — byte-identical logic that lived separately
 * in `BotOverview.tsx` (populated screen) and `FirstRun.tsx` (brand-new screen) until fix-wave
 * item 5. Callers still gate rendering on `sourceCount > 0` themselves (a zero-source bot shows
 * the "Point it at your site" empty state instead of any chip at all).
 *
 * Task 4 (source-detail-diagnostics) added the `deleting` branch, fix round 1 widened it.
 * `STATUS_SEVERITY` ranks `deleting` LEAST severe of all (`packages/shared-types/src/limits.ts`),
 * deliberately, since a source being torn down isn't a problem with the bot — but that means
 * `worstStatus` only reads `'deleting'` itself when EVERY source is deleting, and reads
 * `'ready'` (not `'deleting'`) for the far more common mix of one `ready` source and one
 * `deleting` one, since `ready`'s severity (1) still beats `deleting`'s (0). The first version
 * of this branch (`worstStatus === 'deleting'` alone) missed that second case entirely: a
 * two-source bot mid-delete has `allReady: false` (correct — not every source is ready) but
 * `worstStatus: 'ready'`, which fell through to the generic branch below and rendered a
 * READY-hued, non-animated chip reading "Sources syncing" — green for "nothing is syncing,
 * something is being removed," on the delete path any bot with more than one source takes.
 * `worstStatus === 'ready'` combined with `!allReady` is reachable ONLY via a `deleting`
 * sibling (any other status outranks `ready` and would win `worstStatus` outright instead), so
 * it gets the same treatment as the literal `'deleting'` case.
 *
 * Final review (source-detail-diagnostics, whole-branch review round 2): now built on
 * `effectiveWorstStatus` (`lib/bots-list.ts`) instead of its own inline copy of the same
 * ready+deleting-mix rule — this was the fourth independent re-derivation the reviewer found
 * (`Bots.tsx`'s `rowState`, `BotSwitcher.tsx`'s `botRowView`, and `sourceRollupLabel` were fixed
 * to share it in round 1; this file was missed). Functionally unchanged: `effectiveWorstStatus`
 * already collapses `worstStatus === 'ready' && !allReady` into `'deleting'`, so the explicit
 * `=== 'ready'` branch below is redundant with it and no longer needed.
 *
 * `t: Catalog` lands here in Task 3 (locale-dashboard plan) ahead of any of this function's own
 * literals moving — it's the mechanical, compiler-checked thread-through that lets `t` be a
 * required parameter everywhere before any individual batch has to touch call sites again. Task
 * 7 is the one that swaps `'All ready'`/`'Sources syncing'`/`'A source needs attention'`/
 * `'Removing sources'` for `t.overview.*` keys. */
export function knowledgeChipView(
  knowledge: Pick<BotOverviewDto['knowledge'], 'allReady' | 'worstStatus'>,
  t: Catalog,
): { state: StatusChipState; label: string } {
  if (knowledge.allReady) return { state: 'ready', label: t.overview.knowledgeAllReady };
  const effective = effectiveWorstStatus(knowledge);
  if (effective === 'deleting') {
    return { state: 'deleting', label: t.overview.knowledgeRemoving };
  }
  return {
    state: effective ?? 'pending',
    label: effective === 'error' ? t.overview.knowledgeNeedsAttention : t.overview.knowledgeSyncing,
  };
}

/** SVG polyline `points` for the 14-day sparkline, scaled into a `width`×`height` viewBox. A
 * single-point series (a brand-new bot's first day) can't have a step between points, so it
 * renders a flat point rather than dividing by zero. */
export function sparklinePoints(data: DailyCount[], width: number, height: number): string {
  const max = Math.max(1, ...data.map((d) => d.count));
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  return data
    .map((d, i) => {
      const x = i * step;
      const y = height - (d.count / max) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

/** The same scaling as `sparklinePoints`, for a bare `number[]` series with no per-day date to
 * carry — the All-bots row's 60×22 sparkline (Task 10, handoff README §4) draws from
 * `BotListItemDto['health'].spark`, which is reduced to bare counts because a row this small
 * has no axis to label. Delegates to `sparklinePoints` itself (wrapping each count in a
 * dateless stub) rather than re-deriving the point math a second time. */
export function sparklinePointsFromCounts(counts: number[], width: number, height: number): string {
  return sparklinePoints(
    counts.map((count) => ({ date: '', count })),
    width,
    height,
  );
}

/** The same line, closed down to the baseline into a fillable polygon — the sparkline's area
 * wash under the stroke (handoff: "2px --primary polyline over a --primary 20%→0 area fill"). */
export function sparklineArea(data: DailyCount[], width: number, height: number): string {
  return `${sparklinePoints(data, width, height)} ${width},${height} 0,${height}`;
}

/** Last-7-vs-prior-7 trend over the 14 oldest→newest days. Muted "→" when there's nothing to
 * compare against (prior week had zero messages, or the two weeks tied) — a green/red
 * percentage off a zero baseline would be meaningless. */
export function messagesTrend(data: DailyCount[]): { label: string; tone: 'up' | 'flat' } {
  const prior7 = data.slice(0, 7).reduce((sum, d) => sum + d.count, 0);
  const last7 = data.slice(7).reduce((sum, d) => sum + d.count, 0);
  if (prior7 === 0 || prior7 === last7) return { label: '→', tone: 'flat' };
  const pct = Math.round(((last7 - prior7) / prior7) * 100);
  return pct > 0 ? { label: `↑ ${pct}%`, tone: 'up' } : { label: `↓ ${Math.abs(pct)}%`, tone: 'flat' };
}

/** The "Re-sync all" button's fan-out set — extracted (final review, source-detail-diagnostics)
 * so it has a real test instead of only ever being eyeballed on the Overview screen. Sources
 * already mid-sync can't be resynced (the API 409s — `sources.ts`'s `POST /:sid/resync` guard),
 * so they're filtered out before firing; when EVERY source is ineligible this is empty, which the
 * button disables for — no click can fan out over zero requests and silently report success.
 *
 * `deleting` excluded too: Task 4 made it a new 409 (`SOURCE_DELETING`) on this exact route, and
 * this filter predated that fix landing here. Without it, a bot whose only source was mid-delete
 * showed an enabled Re-sync that fanned out to a single request guaranteed to fail — advice that
 * can never work, since re-syncing a source that's being torn down isn't a real recovery path. */
export function eligibleForResync(sources: SourceDto[] | undefined): SourceDto[] {
  return (sources ?? []).filter(
    (s) => s.status !== 'crawling' && s.status !== 'indexing' && s.status !== 'deleting',
  );
}

/** The sparkline's mono date-end labels ("26 Jul" / "8 Aug", handoff §2) — first and last day
 * of the 14-day window, reusing the rail usage block's own `formatShortDate` rather than a new
 * formatter. `null` on an empty series (shouldn't happen — the API always zero-fills 14
 * entries — but keeps this total rather than indexing an empty array). */
export function sparklineDateLabels(data: DailyCount[]): { start: string; end: string } | null {
  const first = data[0];
  const last = data[data.length - 1];
  if (!first || !last) return null;
  return {
    start: formatShortDate(new Date(first.date)),
    end: formatShortDate(new Date(last.date)),
  };
}

/** Compact "12m"/"1h"/"3h"/"1d" style used only in the Overview's Latest conversations list
 * (handoff §2) — everywhere else in the dashboard uses the more verbose `relativeTime`
 * ("2h ago"). */
export function compactRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}
